import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';

interface User {
  role: 'admin' | 'teacher' | 'student' | 'parent' | 'secretariat';
  permissions?: string[];
}

interface ToolItem {
  label: string;
  path: string;
  icon: string;
  description: string;
  roles: string[];
  requiredPermission?: string;
}

interface SharedToolsMenuProps {
  user: User;
  onNavigate?: (path: string) => void;
}

const SharedToolsMenu = ({ user, onNavigate }: SharedToolsMenuProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  const tools: ToolItem[] = [
    {
      label: 'Clasă online',
      path: '/online-classroom',
      icon: 'Video',
      description: 'Participă sau găzduiește clase virtuale',
      roles: ['teacher', 'student', 'admin'],
    },
    {
      label: 'Centru comunicare',
      path: '/communication-center',
      icon: 'MessageSquare',
      description: 'Mesaje și anunțuri',
      roles: ['admin', 'teacher', 'student', 'parent', 'secretariat'],
    },
    {
      label: 'Comunitate',
      path: '/school-social-platform',
      icon: 'Users',
      description: 'Funcții sociale și comunitate',
      roles: ['admin', 'teacher', 'student', 'parent', 'secretariat'],
    },
    {
      label: 'Gestionare clase',
      path: '/class-management-hub',
      icon: 'School',
      description: 'Gestionează clasele și elevii',
      roles: ['teacher'],
    },
    {
      label: 'Catalog',
      path: '/gradebook-interface',
      icon: 'BookOpen',
      description: 'Vezi și gestionează notele',
      roles: ['teacher', 'admin'],
    },
    {
      label: 'Bibliotecă',
      path: '/library',
      icon: 'Library',
      description: 'Resurse, cărți și proiecte',
      roles: ['admin', 'teacher', 'student', 'parent', 'secretariat'],
    },
    {
      label: 'Documente',
      path: '/documents',
      icon: 'FileText',
      description: 'Documente utile pentru toți',
      roles: ['admin', 'teacher', 'student', 'parent', 'secretariat'],
    },
  ];

  const availableTools = tools.filter((tool) => {
    const hasRoleAccess = tool.roles.includes(user.role);
    const hasPermission =
      !tool.requiredPermission ||
      (user.permissions && user.permissions.includes(tool.requiredPermission));
    return hasRoleAccess && hasPermission;
  });

  const handleNavigation = (path: string) => {
    if (onNavigate) onNavigate(path);
    else navigate(path);
    setIsOpen(false);
  };

  const isActivePath = (path: string) => location.pathname === path;

  if (availableTools.length === 0) return null;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        className="flex items-center gap-2 p-2 sm:px-4 sm:py-2 rounded-md text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-150"
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label="Meniu instrumente"
      >
        <Icon name="Grid3x3" size={18} />
        <span className="hidden sm:inline">Instrumente</span>
        <Icon
          name="ChevronDown"
          size={16}
          className={`hidden sm:inline transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen ? (
        <>
          <div
            className="fixed inset-0 z-[1000]"
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />
          <div className="fixed left-1/2 top-16 -translate-x-1/2 sm:absolute sm:left-auto sm:top-auto sm:right-0 sm:translate-x-0 mt-2 w-[min(96vw,24rem)] sm:w-80 bg-popover border border-border rounded-md shadow-lg z-[1010] animate-scale-in max-h-[70vh] overflow-y-auto">
            <div className="p-3 border-b border-border">
              <h3 className="text-sm font-semibold text-foreground">Instrumente</h3>
              <p className="text-xs text-muted-foreground mt-0.5">Acces rapid la funcții colaborative</p>
            </div>

            <div className="p-2 space-y-1">
              {availableTools.map((tool) => (
                <button
                  key={tool.path}
                  type="button"
                  onClick={() => handleNavigation(tool.path)}
                  className={`group flex items-start gap-3 w-full p-3 rounded-md text-left transition-all duration-150 ${
                    isActivePath(tool.path)
                      ? 'bg-primary text-primary-foreground'
                      : 'hover:bg-muted'
                  }`}
                  aria-current={isActivePath(tool.path) ? 'page' : undefined}
                >
                  <div
                    className={`flex-shrink-0 mt-0.5 ${
                      isActivePath(tool.path)
                        ? 'text-primary-foreground'
                        : 'text-muted-foreground group-hover:text-foreground'
                    }`}
                  >
                    <Icon name={tool.icon} size={20} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p
                      className={`text-sm font-medium mb-0.5 ${
                        isActivePath(tool.path) ? 'text-primary-foreground' : 'text-foreground'
                      }`}
                    >
                      {tool.label}
                    </p>
                    <p
                      className={`text-xs ${
                        isActivePath(tool.path)
                          ? 'text-primary-foreground/80'
                          : 'text-muted-foreground'
                      }`}
                    >
                      {tool.description}
                    </p>
                  </div>
                  {isActivePath(tool.path) ? (
                    <Icon name="Check" size={16} className="flex-shrink-0 mt-1 text-primary-foreground" />
                  ) : null}
                </button>
              ))}
            </div>

            <div className="p-3 border-t border-border bg-muted/50">
              <div className="flex items-start gap-2">
                <Icon name="Info" size={14} className="text-muted-foreground mt-0.5 flex-shrink-0" />
                <p className="text-xs text-muted-foreground">
                  Instrumentele sunt filtrate în funcție de rol și permisiuni
                </p>
              </div>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default SharedToolsMenu;
